import type { RequestStatus } from "./types";

/**
 * A request stops being urgent once it is finished. Sorting an overdue-looking
 * pill onto delivered work is how a dashboard teaches people to ignore its pills.
 */
const OPEN_STATUSES: RequestStatus[] = ["pending", "approved", "in_progress"];
export function isOpen(status: RequestStatus) {
  return OPEN_STATUSES.includes(status);
}

/**
 * Midnight LOCAL, from a plain `YYYY-MM-DD`.
 *
 * `new Date("2026-08-25")` parses as UTC midnight, which in Kuwait (UTC+3) is
 * 03:00 on the 25th — close enough to look right and wrong at the boundary, so
 * a request due today reads as due tomorrow for the first three hours of every
 * day. Splitting the string keeps the comparison in the reader's own calendar.
 */
function localDate(value: string) {
  const [y, m, d] = value.slice(0, 10).split("-").map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
}

function startOfToday() {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), now.getDate());
}

const DAY_MS = 86_400_000;

export type UrgencyLevel = "overdue" | "today" | "soon" | "scheduled" | "none";

export type Urgency = {
  level: UrgencyLevel;
  /** Whole days from today. Negative = past due. */
  days: number;
  label: string;
};

/**
 * The date that is actually LIVE.
 *
 * Once Prismic promises a date at approval, that is the commitment — the date the
 * client originally asked for becomes history. Keying urgency off the request date
 * had a job reading "21 days late" while the promise we made was still eight days
 * away, which trains everyone to ignore the red.
 */
export function operativeDate(request: { due_date: string | null; promised_date: string | null }) {
  return request.promised_date ?? request.due_date;
}

/** Is the live date a Prismic commitment rather than the client's original ask? */
export function isPromised(request: { due_date: string | null; promised_date: string | null }) {
  return Boolean(request.promised_date);
}

/** How a due date reads to the person who is waiting on the work. */
export function dueUrgency(dueDate: string | null, status: RequestStatus): Urgency {
  if (!dueDate) return { level: "none", days: 0, label: "No date set" };
  const due = localDate(dueDate);
  if (!due) return { level: "none", days: 0, label: "No date set" };

  const days = Math.round((due.getTime() - startOfToday().getTime()) / DAY_MS);

  // Finished work keeps its date but loses its alarm. "Finished" is not one thing:
  // canceled and declined work was never delivered, and labelling it "Delivered late"
  // told a client we shipped something we did not.
  if (status === "canceled") return { level: "scheduled", days, label: "Canceled" };
  if (status === "rejected") return { level: "scheduled", days, label: "Declined" };
  if (!isOpen(status)) {
    return { level: "scheduled", days, label: days < 0 ? "Delivered late" : "Delivered" };
  }
  if (days < 0) return { level: "overdue", days, label: days === -1 ? "1 day late" : `${Math.abs(days)} days late` };
  if (days === 0) return { level: "today", days, label: "Due today" };
  if (days === 1) return { level: "soon", days, label: "Due tomorrow" };
  if (days <= 3) return { level: "soon", days, label: `Due in ${days} days` };
  return { level: "scheduled", days, label: `Due in ${days} days` };
}

/**
 * How long something has been sitting. Answers "have you forgotten about me",
 * which is the question a client asks a week before they say it out loud.
 */
export function ageInDays(createdAt: string) {
  const created = new Date(createdAt);
  if (Number.isNaN(created.getTime())) return 0;
  const start = new Date(created.getFullYear(), created.getMonth(), created.getDate());
  return Math.max(0, Math.round((startOfToday().getTime() - start.getTime()) / DAY_MS));
}

export function ageLabel(createdAt: string) {
  const days = ageInDays(createdAt);
  if (days === 0) return "Today";
  if (days === 1) return "1 day";
  if (days < 7) return `${days} days`;
  const weeks = Math.floor(days / 7);
  return weeks === 1 ? "1 week" : `${weeks} weeks`;
}

/** Waiting long enough that someone should say something. */
export function isStale(request: { status: RequestStatus; created_at: string }) {
  return request.status === "pending" && ageInDays(request.created_at) >= 3;
}
